import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AppWindow,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Users,
  Clock,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/utils';
import {
  getMyApps,
  submitAppDelete,
  type AppData,
  type AppRequestData,
} from '@/api/apps';

export default function CreatorAppsPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [apps, setApps] = useState<AppData[]>([]);
  const [pendingRequests, setPendingRequests] = useState<AppRequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Modal states
  const [selectedApp, setSelectedApp] = useState<AppData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getMyApps();
      setApps(result.apps ?? []);
      setPendingRequests(result.pendingRequests ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load apps';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
      // 确保错误时也设置为空数组
      setApps([]);
      setPendingRequests([]);
    } finally {
      setLoading(false);
    }
  }, [toast, t.common.error]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteRequest = async () => {
    if (!selectedApp) return;

    try {
      setSubmitting(true);
      await submitAppDelete(selectedApp.id, { reason: deleteReason || undefined });
      toast({
        title: t.common.success,
        description: t.creator.deleteRequestSubmitted,
      });
      setShowDeleteModal(false);
      setSelectedApp(null);
      setDeleteReason('');
      fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit delete request';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Check if app has pending request
  const hasPendingRequest = (appId: string) => {
    return pendingRequests.some((r) => r.appId === appId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.creator.myApps}</h1>
          <p className="text-gray-500 mt-1">{t.creator.myAppsDesc}</p>
        </div>
        <Link to="/creator/submit">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            {t.creator.submitNewApp}
          </Button>
        </Link>
      </div>

      {/* Pending requests notice */}
      {pendingRequests.length > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">{t.creator.pendingRequestsNotice.replace('{count}', String(pendingRequests.length))}</p>
            <p className="text-sm text-yellow-700 mt-1">
              {t.creator.viewHistory} <Link to="/creator/history" className="underline">{t.creator.submissionHistory}</Link>
            </p>
          </div>
        </div>
      )}

      {/* Apps list */}
      {apps.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AppWindow className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 mb-4">{t.creator.noApps}</p>
            <Link to="/creator/submit">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t.creator.submitFirstApp}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => {
            const isPending = hasPendingRequest(app.id);
            return (
              <Card key={app.id} className={isPending ? 'opacity-75' : ''}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                        {app.iconUrl ? (
                          <img src={app.iconUrl} alt={app.name} className="w-8 h-8 rounded" />
                        ) : (
                          <AppWindow className="w-6 h-6 text-green-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{app.name}</h3>
                        <span className="text-sm text-gray-500">v{app.version}</span>
                      </div>
                    </div>
                    <Badge variant="success">
                      {t.status.approved}
                    </Badge>
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{app.description}</p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {app.categoryTags?.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="default">{tag}</Badge>
                    ))}
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {t.admin.groupCount.replace('{count}', String(app.allowedGroups?.length ?? 0))}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDateTime(app.updatedAt)}
                    </span>
                  </div>

                  {isPending && (
                    <div className="mb-3 p-2 bg-yellow-50 rounded-lg text-sm text-yellow-700">
                      {t.creator.hasPendingRequest}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <a
                      href={app.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary-600 hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t.common.view}
                    </a>
                    <div className="flex-1" />
                    <Link to={`/creator/edit/${app.id}`}>
                      <Button variant="ghost" size="sm" disabled={isPending}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isPending}
                      onClick={() => {
                        setSelectedApp(app);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedApp(null);
          setDeleteReason('');
        }}
        title={t.creator.deleteApp}
        description={t.creator.deleteAppConfirm.replace('{name}', selectedApp?.name || '')}
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-700">
              {t.creator.deleteAppNotice}
            </p>
          </div>
          <Textarea
            label={t.creator.deleteReason}
            placeholder={t.creator.deleteReasonPlaceholder}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            rows={3}
          />
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedApp(null);
              setDeleteReason('');
            }}
            disabled={submitting}
          >
            {t.common.cancel}
          </Button>
          <Button variant="danger" onClick={handleDeleteRequest} disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trash2 className="w-4 h-4 mr-2" />
            {t.creator.submitDeleteRequest}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
